import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Lightbulb, Send, FileText, BookOpen, ShieldAlert, Plus, X } from 'lucide-react';

const ThinkTankResearcher: React.FC = () => {
  const [activeCases, setActiveCases] = useState<any[]>([]);
  const [selectedCase, setSelectedCase] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [outputs, setOutputs] = useState<any[]>([]);
  
  const [formData, setFormData] = useState({
    type: 'research_memo',
    title: '',
    content: '',
    fileUrl: ''
  });

  const fetchActiveCases = async () => {
    try {
      const response = await api.get('/cases');
      // Only show cases where Think Tank is activated
      setActiveCases(response.data.filter((c: any) => c.thinkTankActivated));
    } catch (err) {
      console.error('Error fetching cases:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOutputs = async (caseId: string) => {
    try {
      const response = await api.get(`/think-tank/case/${caseId}`);
      setOutputs(response.data);
    } catch (err) {
      console.error('Error fetching outputs:', err);
    }
  };

  useEffect(() => {
    fetchActiveCases();
  }, []);

  const handleSelectCase = (caseItem: any) => {
    setSelectedCase(caseItem);
    fetchOutputs(caseItem.id);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCase) return;

    try {
      await api.post('/think-tank', { ...formData, caseId: selectedCase.id });
      setFormData({ type: 'research_memo', title: '', content: '', fileUrl: '' });
      fetchOutputs(selectedCase.id);
      alert('Report submitted successfully!');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to submit report');
    }
  };

  if (loading) return <div>Loading active Think Tank cases...</div>;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '2rem', height: 'calc(100vh - 4rem)' }}>
      {/* Case List Sidebar */}
      <div style={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '1rem', borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
          <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#1a365d' }}>
            <Lightbulb size={18} /> Active Think Tank
          </h4>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {activeCases.map((c) => (
            <div 
              key={c.id} 
              onClick={() => handleSelectCase(c)}
              style={{ 
                padding: '1rem', 
                borderBottom: '1px solid #edf2f7', 
                cursor: 'pointer',
                backgroundColor: selectedCase?.id === c.id ? '#ebf8ff' : 'transparent',
                borderLeft: selectedCase?.id === c.id ? '4px solid #2b6cb0' : '4px solid transparent'
              }}
            >
              <p style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#718096' }}>{c.caseId}</p>
              <p style={{ fontSize: '0.9rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>{c.title}</p>
              <p style={{ fontSize: '0.75rem', color: '#4a5568' }}>{c.practiceArea}</p>
            </div>
          ))}
          {activeCases.length === 0 && (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#718096', fontSize: '0.875rem' }}>
              No cases currently require expert research.
            </div>
          )}
        </div>
      </div>

      {/* Main Workspace */}
      <div style={{ overflowY: 'auto', paddingRight: '1rem' }}>
        {selectedCase ? (
          <div>
            <header style={{ marginBottom: '2rem' }}>
              <h2 style={{ fontSize: '1.5rem', color: '#1a365d', marginBottom: '0.5rem' }}>{selectedCase.title}</h2>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <span style={{ fontSize: '0.875rem', backgroundColor: '#edf2f7', padding: '0.2rem 0.6rem', borderRadius: '4px' }}><strong>ID:</strong> {selectedCase.caseId}</span>
                <span style={{ fontSize: '0.875rem', backgroundColor: '#edf2f7', padding: '0.2rem 0.6rem', borderRadius: '4px' }}><strong>Area:</strong> {selectedCase.practiceArea}</span>
              </div>
            </header>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: '2rem' }}>
              {/* Submission Form */}
              <section style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <h3 style={{ fontSize: '1.1rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Plus size={20} /> Submit Research/Strategy
                </h3>
                <form onSubmit={handleSubmit}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 'bold' }}>Report Type</label>
                      <select 
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                        style={inputStyle}
                      >
                        <option value="research_memo">Legal Research Memo</option>
                        <option value="strategy_document">Strategy Document</option>
                        <option value="precedent_list">Precedent Analysis</option>
                        <option value="expert_opinion">Expert Opinion</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 'bold' }}>Title</label>
                      <input 
                        type="text" 
                        placeholder="e.g. Constitutional Challenge Strategy" 
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        required
                        style={inputStyle}
                      />
                    </div>
                  </div>
                  
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 'bold' }}>Detailed Analysis</label>
                    <textarea 
                      rows={12}
                      placeholder="Write your expert findings here..."
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      required
                      style={{ ...inputStyle, fontFamily: 'monospace' }}
                    />
                  </div>

                  <button type="submit" style={{ width: '100%', padding: '0.75rem', backgroundColor: '#d69e2e', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                    <Send size={18} /> Publish to Case Think Tank
                  </button>
                </form>
              </section>

              {/* History Sidebar */}
              <section>
                <h3 style={{ fontSize: '1.1rem', marginBottom: '1.5rem' }}>Existing Reports</h3>
                <div style={{ display: 'grid', gap: '1rem' }}>
                  {outputs.map((out) => (
                    <div key={out.id} style={{ backgroundColor: 'white', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        {getIcon(out.type)}
                        <span style={{ fontSize: '0.75rem', fontWeight: 'bold', textTransform: 'uppercase', color: '#718096' }}>{out.type.replace('_', ' ')}</span>
                      </div>
                      <p style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>{out.title}</p>
                      <p style={{ fontSize: '0.8rem', color: '#4a5568', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', marginBottom: '1rem' }}>{out.content}</p>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.7rem', color: '#a0aec0' }}>
                        <span>By {out.researcher?.name}</span>
                        <span>{new Date(out.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                  {outputs.length === 0 && (
                    <div style={{ padding: '2rem', textAlign: 'center', color: '#cbd5e0', backgroundColor: '#f8fafc', borderRadius: '8px', border: '2px dashed #edf2f7' }}>
                      No reports yet for this case.
                    </div>
                  )}
                </div>
              </section>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#a0aec0' }}>
            <Lightbulb size={64} style={{ marginBottom: '1rem', opacity: 0.2 }} />
            <p>Select a case from the sidebar to begin expert research.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const getIcon = (type: string) => {
  switch (type) {
    case 'research_memo': return <BookOpen size={16} color="#3182ce" />;
    case 'strategy_document': return <FileText size={16} color="#38a169" />;
    case 'precedent_list': return <ShieldAlert size={16} color="#e53e3e" />;
    default: return <Lightbulb size={16} color="#d69e2e" />;
  }
};

const inputStyle = { width: '100%', padding: '0.625rem', borderRadius: '4px', border: '1px solid #e2e8f0' };

export default ThinkTankResearcher;
